import { catchError, concat, forkJoin, from, map, Observable, of, take, tap } from 'rxjs';
import { capitalizeFirstLetter, isArray } from '@components/Utility/index.js';
import { shareReplay, switchMap, toArray } from 'rxjs/operators';
import { ENV } from '@components/Environment.js';
class DependencyInjectionContainer {
    constructor() {
        this.componentsFactories = {};
        this.singletonFactories = {};
        this.singletons = {};
        this.lazyFactories = {}; // Added for lazy components
        this.loadedScripts = {};
        this.classConstructors = {};
        this.awaitingDependencies = new Map();
        this.customDependencies = new Set();
        this.loadedScripts = {};
    }
    getClassConstructor(className, loadCustom = true) {
        if (loadCustom && this.classConstructors[`Custom_${className}`]) {
            return this.classConstructors[`Custom_${className}`];
        }
        return this.classConstructors[className];
    }
    isConstructor(func) {
        return (typeof func === 'function' &&
            /^class(?:\s|\{)/.test(Function.prototype.toString.call(func)) // check for "class ..." and "class{". the second pattern is necessary for minified code.
        );
    }
    register(key, factoryOrConstructor) {
        let factory;
        if (this.isConstructor(factoryOrConstructor)) {
            this.classConstructors[key] = factoryOrConstructor;
            factory = (...args) => new factoryOrConstructor(...args);
        }
        else {
            factory = factoryOrConstructor;
        }
        // Existing logic for handling 'Custom' keys
        if (key.startsWith('Custom_')) {
            const coreKey = key.replace('Custom_', '');
            this.componentsFactories[`Core${coreKey}`] = this.componentsFactories[coreKey];
            this.componentsFactories[coreKey] = factory;
        }
        this.componentsFactories[key] = factory;
    }
    /**
     * Register a singleton service in the container.
     * The service will be instantiated once, and the same instance will be returned on subsequent requests.
     * @param key - Identifier for the service.
     * @param factoryOrConstructor
     */
    registerSingleton(key, factoryOrConstructor) {
        let factory;
        if (this.isConstructor(factoryOrConstructor)) {
            this.classConstructors[key] = factoryOrConstructor;
            factory = (...args) => new factoryOrConstructor(...args);
        }
        else {
            factory = factoryOrConstructor;
        }
        if (key.startsWith('Custom_')) {
            const coreKey = key.replace('Custom_', '');
            this.singletonFactories[`Core${coreKey}`] = this.singletonFactories[coreKey];
            this.singletonFactories[coreKey] = factory;
            delete this.singletons[coreKey];
        }
        this.singletonFactories[key] = factory;
        if (this.singletons[key]) {
            delete this.singletons[key];
        }
    }
    registerStatic(key, factory) {
        key = key + '_static';
        this.singletonFactories[key] = factory;
        if (this.singletons[key]) {
            // remove our reference to the old singleton as we have registered something new. This should be uncommon (currently only for changing screen manager modes)
            delete this.singletons[key];
        }
    }
    registerLazy(key, factory) {
        if (key.startsWith('Custom_')) {
            const coreKey = key.replace('Custom_', '');
            this.lazyFactories[`Core${key}`] = this.lazyFactories[coreKey]; // maintain a reference to the core factory so we can still access it if needed maybe for debugging or something. 
            this.lazyFactories[coreKey] = { factory, isSingleton: false };
        }
        this.lazyFactories[key] = { factory, isSingleton: false };
    }
    registerLazySingleton(key, factory) {
        if (key.startsWith('Custom_')) {
            const coreKey = key.replace('Custom_', '');
            this.lazyFactories[`Core${key}`] = this.lazyFactories[coreKey]; // maintain a reference to the core factory so we can still access it if needed maybe for debugging or something. 
            this.lazyFactories[coreKey] = { factory, isSingleton: true };
        }
        this.lazyFactories[key] = { factory, isSingleton: true };
    }
    resolveLazy(key, ...args) {
        if (this.singletons[key]) {
            const component = this.singletons[key];
            return of(component).pipe(take(1));
        }
        else if (this.lazyFactories[key]) {
            return new Observable(subscriber => {
                // Check if it's a singleton and already instantiated
                if (this.lazyFactories[key].isSingleton && this.singletons[key]) {
                    subscriber.next(this.singletons[key]);
                    subscriber.complete();
                }
                else {
                    this.lazyFactories[key].factory(...args).then(component => {
                        if (this.isConstructor(component)) {
                            this.classConstructors[key] = component; // store class constructor for custom code to be able to extend it
                            if (this.componentsFactories[key]) {
                                // this would be primarily used for a custom hook to customize lazy loaded components like Scheduler. 
                                // they just needed to wait for this classConstructor to get set from the lazy loading before they could extend it. 
                                component = this.componentsFactories[key](...args);
                            }
                            else {
                                component = new component(...args);
                            }
                        }
                        // If it's a singleton, cache the instance
                        if (this.lazyFactories[key].isSingleton) {
                            this.singletons[key] = component;
                        }
                        subscriber.next(component);
                        subscriber.complete();
                    }).catch(error => subscriber.error(error));
                }
            }).pipe(take(1));
        }
        else {
            try {
                const component = this.resolve(key, ...args);
                return of(component).pipe(take(1));
            }
            catch (e) {
                /*return new Observable<T>(subscriber => {
                    subscriber.error(new Error(`Lazy component factory for "${key}" not found.`));
                });*/
                console.warn(`Lazy component not registered. Attempting to load external dependency file. "${key}". ${e.message}`);
                // we can finally attempt to load a dependency. dependencies dont currently use arguments. we could add that later if needed
                return this.loadDependencies(key).pipe(take(1), switchMap(() => {
                    if (this.lazyFactories[key]) {
                        return this.resolveLazy(key, ...args);
                    }
                    return of(this.resolve(key, ...args));
                }), catchError(_error => {
                    return new Observable(subscriber => {
                        subscriber.error(new Error(`Error loading component "${key}". ${e.message}`));
                    });
                }));
            }
        }
    }
    /**
     * Returns the singleton instance of a given Component.
     * `getInstance` is a common convention in web programming, so this will make it clear we are expecting a singleton.
     * @param key
     * @param args
     */
    getInstance(key, ...args) {
        if (this.singletons[key]) {
            return this.singletons[key];
        }
        else if (this.singletonFactories[key]) {
            return this.singletons[key] = this.singletonFactories[key](...args);
        }
        throw new Error(`Singleton ${key} not found.`);
    }
    getStatic(key, ...args) {
        key = key + '_static';
        if (this.singletons[key]) {
            return this.singletons[key];
        }
        else if (this.singletonFactories[key]) {
            return this.singletons[key] = this.singletonFactories[key](...args);
        }
        throw new Error(`Singleton ${key} not found.`);
    }
    getRecordLogicClass(tableName) {
        if (!tableName) {
            return of(this.getInstance('RecordLogic'));
        }
        tableName = capitalizeFirstLetter(tableName);
        return this.resolveLazy(tableName).pipe(catchError(_error => {
            return of(this.getInstance('RecordLogic'));
        }));
    }
    isSingletonRegistered(key) {
        return Reflect.has(this.singletonFactories, key);
    }
    isRegistered(key) {
        return Reflect.has(this.componentsFactories, key) || Reflect.has(this.singletonFactories, key);
    }
    parseComponentDefinition(componentDef) {
        const component = this.new(componentDef.componentType, componentDef.id);
        component.rootElement = document.createElement(component.rootElementType);
        if (componentDef.captionKey) {
            component.captionKey = componentDef.captionKey;
        }
        component.propertiesLoad(componentDef.componentProperties);
        return component;
    }
    /**
     * Returns a new instance of a given Component.
     * `new` is used here to make it clear we are expecting to create a new instance of a Component.
     * @param key
     * @param args
     */
    new(key, ...args) {
        if (this.componentsFactories[key]) {
            return this.componentsFactories[key](...args);
        }
        else if (this.classConstructors[key]) {
            return new this.classConstructors[key](...args);
        }
        throw new Error(`Component ${key} not found.`);
    }
    resolve(key, ...args) {
        if (this.singletons[key]) {
            return this.singletons[key];
        }
        else if (this.singletonFactories[key]) {
            return this.singletons[key] = this.singletonFactories[key](...args);
        }
        else if (this.componentsFactories[key]) {
            return this.componentsFactories[key](...args);
        }
        else if (this.classConstructors[key]) {
            return new this.classConstructors[key](...args);
        }
        throw new Error(`Component/Singleton ${key} not found.`);
    }
    awaitDependencies(dependency, extraDependenciesCsv, options = {}) {
        if (isArray(extraDependenciesCsv)) {
            extraDependenciesCsv = extraDependenciesCsv.join(',');
        }
        if (this.awaitingDependencies.has(dependency)) {
            return this.awaitingDependencies.get(dependency);
        }
        this.awaitingDependencies.set(dependency, this.loadDependencies(extraDependenciesCsv, options).pipe(shareReplay(1)));
        return this.awaitingDependencies.get(dependency);
    }
    registerCustomDependency(dependency) {
        this.customDependencies.add(dependency);
    }
    loadDependencies(dependenciesCsv, options = {}) {
        const dependencies = dependenciesCsv.split(',');
        const dependencySuccessMap = {};
        // maybe instead of the sequential flag we can use internal arrays for chained dependencies and then use concat for those within the forkJoin.
        const observables = dependencies.map(path => {
            let folder = 'dependencies';
            let dep = path;
            if (path.indexOf('/') > -1) {
                // Split the path by '/'
                const parts = path.split('/');
                dep = parts.pop();
                folder = parts.join('/');
            }
            return from(this.loadJSFile(dep, folder)).pipe(switchMap(() => {
                if (this.awaitingDependencies.has(dep)) {
                    console.log(`DEPENDENCY ${dep} IS AWAITING ADDITIONAL DEPENDENCIES`);
                    return this.awaitingDependencies.get(dep);
                }
                else {
                    return of(true);
                }
            }), switchMap(() => {
                if (this.customDependencies.has(`Custom_${dep}`)) {
                    console.log(`DEPENDENCY ${dep} IS AWAITING CUSTOM DEPENDENCIES`);
                    return this.loadDependencies(`custom/${folder}/Custom_${dep}`);
                }
                else {
                    return of(true);
                }
            }), tap({
                next: () => { },
                error: (_error) => {
                    throw new Error(`Failed to load script: ${dep.trim()}`);
                }
            }), map(() => dep.trim()));
        });
        let obs;
        if (options.sequential) {
            obs = concat(...observables).pipe(toArray()); // Combine sequentially and collect into an array
        }
        else {
            obs = forkJoin(observables); // Combine in parallel and return as an array
        }
        return obs.pipe(take(1), map(loadedDeps => {
            loadedDeps.forEach(dep => {
                this.awaitingDependencies.delete(dep);
                dependencySuccessMap[dep] = true;
                // instances[dep] = this.resolve<BaseClass>(dep); // Move this resolve into resolveLazy, loadDependencies should just emit a boolean when its done. 
            });
            return dependencySuccessMap;
        }), catchError(error => {
            return new Observable(subscriber => {
                subscriber.error(new Error(`Error loading dependencies: ${error.message}`));
            });
        }));
    }
    /**
     * Loads a dependency class (external class not compiled in framework) by name. Specify the folder it resides in.
     *
     * @param {string} dependency - The name of the JavaScript class/file to load.
     * @param folder
     * @return {Observable<any>} - An Observable that emits a boolean value indicating whether the script was loaded successfully.
     */
    loadJSFile(dependency, folder = 'dependencies') {
        const caselessDependency = dependency.toLowerCase();
        if (Reflect.has(this.loadedScripts, caselessDependency)) {
            if (this.loadedScripts[caselessDependency] instanceof Observable) {
                // in case we have already requested this dependency, they can share the same observable. 
                return this.loadedScripts[caselessDependency];
            }
            else {
                return of(this.loadedScripts[caselessDependency]);
            }
        }
        else {
            const basePath = `${location.origin}/js/${folder}/`;
            const src = `${basePath}${dependency.trim()}.js`;
            this.loadedScripts[caselessDependency] = loadScript(src, true).pipe(map((result) => {
                this.loadedScripts[caselessDependency] = result !== '404';
                return this.loadedScripts[caselessDependency];
            }), shareReplay({ bufferSize: 1, refCount: true }) // if 2 things rapidly request same dependency, share so we don't load the script twice
            );
            return this.loadedScripts[caselessDependency];
        }
    }
}
export function getInstance(key, ...args) {
    return DIContainer.getInstance(key, ...args);
}
const DIContainer = new DependencyInjectionContainer();
export default DIContainer;
export function loadScript(rawUrl, silentError = false) {
    const version = ENV('clientVersion') + (ENV('hotfixVersion') ? `.${ENV('hotfixVersion')}` : '');
    // ---- Normalize URL with version parameter ----
    const u = new URL(rawUrl, window.location.origin);
    // Only add version if the caller didn't specify one
    if (!u.searchParams.has('v')) {
        u.searchParams.set('v', version);
    }
    const url = u.toString();
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
        return of(url);
    }
    return new Observable(function (observer) {
        const head = document.getElementsByTagName('head')[0];
        const script = document.createElement('script');
        script.src = url;
        const onLoadOrError = function (event) {
            // Remove event listeners to avoid memory leaks
            script.removeEventListener('load', onLoadOrError);
            script.removeEventListener('error', onLoadOrError);
            if (event.type === 'load') {
                observer.next(url);
                observer.complete();
            }
            else {
                if (silentError) {
                    observer.next('404');
                    observer.complete();
                    console.warn(`Failed to load script at ${url}.`);
                }
                else {
                    observer.error(new Error(`Failed to load script at ${url}`));
                }
            }
        };
        // Add event listeners
        script.addEventListener('load', onLoadOrError);
        script.addEventListener('error', onLoadOrError);
        // Begin loading
        head.appendChild(script);
    });
}
